import { shopifyIntegrationConfigSchema } from "schemas/shopifyIntegration";
import { z } from "zod";

export const integrationSchema = z
  .object({
    id: z.string().uuid(),
    type: z.enum([
      "ShopifyIntegrationConfig",
      "Klaviyo",
      "GA4",
      "GoogleTagManager",
      "Hotjar",
      "MicrosoftClarity",
      "Snapchat",
      "TikTok",
      "Triplewhale",
      "Pinterest",
      "Reddit",
    ]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    deletedAt: z.coerce.date().nullish(),
    workspaceId: z.string().uuid(),
    shopifyIntegrationConfig: shopifyIntegrationConfigSchema.nullable(),
    projectId: z.string().uuid().nullish(),
    config: z.any(),
  })
  .describe("Integration");

// NOTE (Max, 2025-07-29): The only difference with the integrationSchema is that
// shopifyIntegrationConfig is required.
// TODO (Max, 2025-07-30, REPL-18291): We should be using ReploIntegration, & accessing the config using
// reploIntegration.config.
export const shopifyIntegrationSchema = integrationSchema
  .omit({
    shopifyIntegrationConfig: true,
  })
  .extend({
    shopifyIntegrationConfig: shopifyIntegrationConfigSchema,
  })
  .describe("ShopifyIntegration");

export const klaviyoConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Public API Key is required"),
  })
  .describe("KlaviyoConfig");

export const ga4ConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^G-[\dA-Z]{10}$/, "Measurement ID must be in format G-XXXXXXXXXX")
      .min(1, "Measurement ID is required"),
  })
  .describe("GA4Config");

export const gtmConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^GTM-[\dA-Z]{7,8}$/, "Container ID must be in format GTM-XXXXXXX")
      .min(1, "Container ID is required"),
  })
  .describe("GTMConfig");

export const hotjarConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^\d+$/, "Site ID must be numeric")
      .min(1, "Site ID is required"),
  })
  .describe("HotjarConfig");

export const clarityConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Project ID is required"),
  })
  .describe("ClarityConfig");

export const snapchatConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("SnapchatConfig");

export const tiktokConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("TikTokConfig");

export const pinterestConfigSchema = z
  .object({
    integrationKey: z
      .string()
      .regex(/^\d+$/, "Tag ID must be numeric")
      .min(1, "Tag ID is required"),
  })
  .describe("PinterestConfig");

export const redditConfigSchema = z
  .object({
    integrationKey: z.string().min(1, "Pixel ID is required"),
  })
  .describe("RedditConfig");

const baseIntegrationFields = integrationSchema.omit({
  config: true,
  type: true,
  shopifyIntegrationConfig: true,
});

export const klaviyoIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Klaviyo"),
    config: klaviyoConfigSchema,
  })
  .describe("KlaviyoIntegration");

export const shopifyIntegrationConfigIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("ShopifyIntegrationConfig"),
    config: shopifyIntegrationConfigSchema,
  })
  .describe("ShopifyIntegrationConfigIntegration");

export const ga4IntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("GA4"),
    config: ga4ConfigSchema,
  })
  .describe("GA4Integration");

export const gtmIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("GoogleTagManager"),
    config: gtmConfigSchema,
  })
  .describe("GTMIntegration");

export const hotjarIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Hotjar"),
    config: hotjarConfigSchema,
  })
  .describe("HotjarIntegration");

export const clarityIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("MicrosoftClarity"),
    config: clarityConfigSchema,
  })
  .describe("ClarityIntegration");

export const snapchatIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Snapchat"),
    config: snapchatConfigSchema,
  })
  .describe("SnapchatIntegration");

export const tiktokIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("TikTok"),
    config: tiktokConfigSchema,
  })
  .describe("TikTokIntegration");

export const triplewhaleIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Triplewhale"),
    config: tiktokConfigSchema,
  })
  .describe("TriplewhaleIntegration");

export const pinterestIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Pinterest"),
    config: pinterestConfigSchema,
  })
  .describe("PinterestIntegration");

export const redditIntegrationSchema = baseIntegrationFields
  .extend({
    type: z.literal("Reddit"),
    config: redditConfigSchema,
  })
  .describe("RedditIntegration");

export const reploIntegrationSchema = z
  .discriminatedUnion("type", [
    klaviyoIntegrationSchema,
    ga4IntegrationSchema,
    gtmIntegrationSchema,
    hotjarIntegrationSchema,
    clarityIntegrationSchema,
    snapchatIntegrationSchema,
    tiktokIntegrationSchema,
    triplewhaleIntegrationSchema,
    pinterestIntegrationSchema,
    redditIntegrationSchema,
    shopifyIntegrationConfigIntegrationSchema,
  ])
  .describe("ReploIntegration");
